/**
 * Console accounts: who can log in and with what role. Admin-only; the
 * API answers 403 for anyone else, so the nav hides it for them.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { api, type MeResponse } from '../api/client'
import { fmtRelative } from '../lib/format'

type UserRow = { id: number; email: string; role: string; created_at: string; last_login_at: string | null; section_access: string[]; can_view_member_activity: boolean }
type UserList = { items: UserRow[]; roles: Record<string, string>; sections: { slug: string; label: string; path: string }[] }

const ROLE_LABEL: Record<string, string> = { admin: 'Admin', editor: 'Editor (Editorial Desk)', writer: 'Writer (Editorial Desk)', operator: 'Customer Support', desk: 'Subscriber Desk', viewer: 'Viewer' }

export function UsersPage() {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['users'], queryFn: () => api.get<UserList>('/api/users') })
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000 })
  const refresh = () => qc.invalidateQueries({ queryKey: ['users'] })
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('editor')
  const [password, setPassword] = useState('')
  const create = useMutation({
    mutationFn: () => api.post<UserRow>('/api/users', { email, role, password }),
    onSuccess: () => { setEmail(''); setPassword(''); refresh() },
  })
  const roles = q.data?.roles ?? {}
  return (
    <div className="p-8 space-y-6 max-w-[1100px]">
      <header className="border-b border-ink-500 pb-6">
        <div className="label mb-1">Console</div>
        <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">Users</h1>
        <p className="label mt-2 max-w-2xl normal-case tracking-normal">
          Who can sign in to this console. An editor sees only Editorial Desk, and only the titles ticked here; a writer the same, but an editor publishes what they write. An admin sees everything, including this page.
        </p>
      </header>

      <Panel label="Add an account">
        <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); create.mutate() }}>
          <label className="block">
            <span className="label">Email</span>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off"
                   className="mt-1 block bg-ink-900 border border-ink-600 text-paper text-[14px] p-2 w-72" />
          </label>
          <label className="block">
            <span className="label">Role</span>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="mt-1 block bg-ink-900 border border-ink-600 text-paper text-[14px] p-2">
              {Object.keys(roles).map((r) => <option key={r} value={r}>{ROLE_LABEL[r] ?? r}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="label">Password (10+ characters)</span>
            <input type="password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"
                   className="mt-1 block bg-ink-900 border border-ink-600 text-paper text-[14px] p-2 w-60" />
          </label>
          <Button type="submit" disabled={create.isPending || !email || password.length < 10}>{create.isPending ? 'Adding…' : 'Add account'}</Button>
        </form>
        <p className="label mt-2">{roles[role]}</p>
        {create.error && <p className="text-red-300 text-[13px] mt-2">{String(create.error)}</p>}
      </Panel>

      <Panel label="Accounts">
        <table className="w-full text-[13px]">
          <thead><tr className="label text-left"><th className="py-1">Email</th><th>Role</th><th>Titles</th><th>Created</th><th>Last sign-in</th><th /></tr></thead>
          <tbody>
            {(q.data?.items ?? []).map((u) => <UserRowView key={u.id} u={u} roles={roles} sections={q.data?.sections ?? []} isMe={me?.email === u.email} onChange={refresh} />)}
          </tbody>
        </table>
      </Panel>
    </div>
  )
}

function UserRowView({ u, roles, sections, isMe, onChange }: { u: UserRow; roles: Record<string, string>; sections: { slug: string; label: string; path: string }[]; isMe: boolean; onChange: () => void }) {
  const [pw, setPw] = useState('')
  const [showPw, setShowPw] = useState(false)
  const patch = useMutation({ mutationFn: (body: { role?: string; password?: string; section_access?: string[]; can_view_member_activity?: boolean }) => api.patch<UserRow>(`/api/users/${u.id}`, body), onSuccess: () => { setPw(''); setShowPw(false); onChange() } })
  const del = useMutation({ mutationFn: () => api.del<void>(`/api/users/${u.id}`), onSuccess: onChange })
  const err = patch.error ?? del.error
  return (
    <tr className="border-t border-ink-700 align-top">
      <td className="py-2 text-paper">{u.email}{isMe && <span className="label ml-2">(you)</span>}</td>
      <td className="py-2">
        <select value={u.role} disabled={isMe || patch.isPending} onChange={(e) => patch.mutate({ role: e.target.value })}
                className="bg-ink-900 border border-ink-600 text-paper text-[13px] p-1">
          {Object.keys(roles).map((r) => <option key={r} value={r}>{ROLE_LABEL[r] ?? r}</option>)}
        </select>
      </td>
      <td className="py-2">
        {u.role === 'admin'
          ? <span className="label">All titles</span>
          : <span className="inline-flex flex-wrap gap-3">
              {sections.map((s) => (
                <label key={s.slug} className="inline-flex items-center gap-1 text-[13px] text-paper">
                  <input type="checkbox" checked={u.section_access.includes(s.slug)} disabled={patch.isPending}
                         onChange={(e) => patch.mutate({ section_access: e.target.checked
                           ? [...u.section_access, s.slug] : u.section_access.filter((x) => x !== s.slug) })} />
                  {s.label}
                </label>
              ))}
            </span>}
        {u.role !== 'editor' && u.role !== 'writer' && <><label className="flex items-center gap-1 text-[13px] text-paper mt-2">
          <input type="checkbox" checked={u.can_view_member_activity} disabled={patch.isPending}
                 onChange={(e) => patch.mutate({ can_view_member_activity: e.target.checked })} />
          Can view member reading history
        </label>
        <p className="label mt-1 max-w-xs">Lets this account see named members' reading history on Visitors pages. Admins always can, once the switch is on.</p></>}
      </td>
      <td className="py-2 text-ink-200">{fmtRelative(u.created_at)}</td>
      <td className="py-2 text-ink-200">{u.last_login_at ? fmtRelative(u.last_login_at) : 'never'}</td>
      <td className="py-2 text-right whitespace-nowrap">
        {showPw
          ? <span className="inline-flex items-center gap-2">
              <input type="password" minLength={10} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="new password (10+)" autoComplete="new-password"
                     className="bg-ink-900 border border-ink-600 text-paper text-[13px] p-1 w-48" />
              <Button size="sm" disabled={pw.length < 10 || patch.isPending} onClick={() => patch.mutate({ password: pw })}>Save</Button>
              <Button size="sm" variant="outline" onClick={() => { setShowPw(false); setPw('') }}>Cancel</Button>
            </span>
          : <span className="inline-flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowPw(true)}>Reset password</Button>
              {!isMe && <Button size="sm" variant="danger" disabled={del.isPending} onClick={() => { if (window.confirm(`Delete ${u.email}? They will no longer be able to sign in.`)) del.mutate() }}>Delete</Button>}
            </span>}
        {err && <div className="text-red-300 text-[12px] mt-1">{String(err)}</div>}
      </td>
    </tr>
  )
}
